import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BadgeStaleness } from "@/components/ui/badge-staleness";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EntitySwitcher } from "@/components/EntitySwitcher";
import { PortfolioSummaryCards } from "@/components/portfolio/PortfolioSummaryCards";
import { AlternativesTable } from "@/components/portfolio/AlternativesTable";
import { CashFlowsTab } from "@/components/portfolio/CashFlowsTab";
import { AlertsTab } from "@/components/portfolio/AlertsTab";
import { useTenant } from "@/hooks/useTenant";
import { useEntity } from "@/hooks/useEntity";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { HoldingsDetailDrawer } from "@/components/portfolio/HoldingsDetailDrawer";
import { AltInspectorDrawer } from "@/components/portfolio/AltInspectorDrawer";
import { chartColors, semanticColors } from "@/lib/theme/chartTheme";

// Type definitions
interface HoldingData {
  name?: string;
  symbol?: string;
  mv?: number;
  cost?: number;
  qty?: number;
  ccy?: string;
  accounts?: { type?: string; custodian?: string };
}

interface PositionData {
  symbol: string;
  name: string;
  account: string;
  mv: number;
  cost: number;
  unrealizedGL: number;
  qty: number;
  ccy?: string;
  isAlternative?: boolean;
  altId?: string;
}

interface AltMetricData {
  alt_id: string;
  computed_nav?: number;
  called_cum?: number;
  period_end: string;
}

// Asset class colors mapping
const assetClassColors: Record<string, string> = {
  "Cash & Equivalents": "bg-blue-500",
  "Public Equity": "bg-green-500",
  "Fixed Income": "bg-purple-500",
  "Private Credit": "bg-orange-500",
  "Private Equity": "bg-red-500",
  "Venture Capital": "bg-yellow-500",
  "Hedge Funds": "bg-pink-500",
  "Real Estate": "bg-teal-500",
  "Other": "bg-gray-500",
};

// Helper to classify holdings into asset classes based on name/symbol heuristics
const classifyAssetClass = (holding: { name?: string; symbol?: string }, accountType?: string): string => {
  const name = (holding.name || '').toLowerCase();
  const symbol = (holding.symbol || '').toUpperCase();

  // Cash detection
  if (symbol === 'USD' || symbol === 'SGD' || symbol === 'EUR' || symbol === 'GBP' ||
      name.includes('cash') || name.includes('money market') || name.includes('mmkt') ||
      accountType === 'dda' || accountType === 'mmda') {
    return 'Cash & Equivalents';
  }

  // Private Equity
  if (name.includes('buyout') || name.includes('private equity') || name.includes('pe fund') ||
      name.includes('growth equity') || name.includes('secondaries') || name.includes('infrastructure') ||
      symbol.includes('BUYOUT') || symbol.includes('PE-')) {
    return 'Private Equity';
  }

  // Venture Capital
  if (name.includes('venture') || name.includes('vc fund') || name.includes('early stage') ||
      symbol.includes('VC-')) {
    return 'Venture Capital';
  }

  // Private Credit
  if (name.includes('direct lending') || name.includes('private credit') || name.includes('mezzanine') ||
      name.includes('distressed debt') || symbol.includes('PCRED')) {
    return 'Private Credit';
  }

  // Hedge Funds
  if (name.includes('hedge') || name.includes('macro') || name.includes('long/short') ||
      name.includes('event driven') || symbol.includes('HF')) {
    return 'Hedge Funds';
  }

  // Real Estate
  if (name.includes('reit') || name.includes('real estate') || name.includes('property') ||
      symbol.includes('RE-')) {
    return 'Real Estate';
  }

  // Fixed Income / Bonds
  if (name.includes('bond') || name.includes('treasury') || name.includes('fixed income') ||
      name.includes('municipal') || name.includes('corporate') || symbol.includes('BOND')) {
    return 'Fixed Income';
  }

  // Default to Public Equity for known stock tickers
  if (symbol.match(/^[A-Z]{1,5}$/) && !name.includes('fund')) {
    return 'Public Equity';
  }

  // ETFs and Public Funds
  if (name.includes('etf') || name.includes('spdr') || name.includes('invesco') ||
      symbol === 'SPY' || symbol === 'QQQ') {
    return 'Public Equity';
  }

  // Account type based classification
  if (accountType === 'fund') {
    return 'Private Equity';
  }
  if (accountType === 'brokerage') {
    return 'Public Equity';
  }

  return 'Other';
};

// Liabilities placeholder (could be fetched separately)
const liabilities = {
  class: "Liabilities",
  mv: 0,
  pct: 0,
  accounts: 0,
  color: "bg-gray-500"
};

// Helper to check if an asset class is an alternative investment
const isAlternativeAssetClass = (assetClass: string) => {
  return [
    "Private Equity",
    "Venture Capital", 
    "Private Credit",
    "Hedge Funds",
    "Real Estate"
  ].includes(assetClass);
};

export default function Holdings() {
  const { orgId, familyId, currentOrg } = useTenant();
  const { entities } = useEntity();
  const [searchParams, setSearchParams] = useSearchParams();
  const [asOf] = useState(new Date().toISOString().split('T')[0]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [navGranularity, setNavGranularity] = useState<"monthly" | "quarterly" | "yearly">("monthly");
  const [selectedPosition, setSelectedPosition] = useState<Record<string, unknown> | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [altDrawerOpen, setAltDrawerOpen] = useState(false);
  const [selectedAlt, setSelectedAlt] = useState<{ altId: string; vehicleName: string } | null>(null);
  const [entityId, setEntityId] = useState<string | null>(null);

  // Tab state - default to 'holdings', support URL parameter
  const currentTab = searchParams.get('tab') || 'holdings';
  const setCurrentTab = (tab: string) => {
    setSearchParams({ tab });
  };

  const selectedEntity = entities.find(e => e.id === entityId);

  // Fetch alt vehicles for summary cards
  const { data: altVehicles } = useQuery({
    queryKey: ['alt-vehicles-summary', orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];
      let query = supabase
        .from('alt_vehicles')
        .select('id, commitment')
        .eq('org_id', orgId);

      if (familyId) {
        query = query.eq('family_id', familyId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId
  });

  // Fetch latest alt metrics for summary
  const altVehicleIds = altVehicles?.map(v => v.id) || [];
  const { data: altMetrics } = useQuery({
    queryKey: ['alt-metrics-summary', altVehicleIds],
    queryFn: async () => {
      if (altVehicleIds.length === 0) return [];
      const { data, error } = await supabase
        .from('alt_period_metrics')
        .select('alt_id, period_end, computed_nav, called_cum')
        .in('alt_id', altVehicleIds)
        .order('period_end', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: altVehicleIds.length > 0
  });

  // Fetch alt vehicles with their latest metrics for the overview table
  // NOTE: Must be defined BEFORE altsSummary useMemo which depends on it
  const { data: altVehiclesWithMetrics } = useQuery({
    queryKey: ['alt-vehicles-with-metrics', orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];

      let query = supabase
        .from('alt_vehicles')
        .select('id, vehicle_name, manager_name, commitment, currency')
        .eq('org_id', orgId);

      if (familyId) {
        query = query.eq('family_id', familyId);
      }

      const { data: vehicles, error } = await query;
      if (error) throw error;
      if (!vehicles || vehicles.length === 0) return [];

      // Fetch latest metrics for each vehicle
      const vehicleIds = vehicles.map(v => v.id);
      const { data: metrics } = await supabase
        .from('alt_period_metrics')
        .select('alt_id, computed_nav, called_cum, period_end')
        .in('alt_id', vehicleIds)
        .order('period_end', { ascending: false });

      // Get latest metric per vehicle
      const latestMetrics = new Map<string, AltMetricData>();
      metrics?.forEach(m => {
        if (!latestMetrics.has(m.alt_id)) {
          latestMetrics.set(m.alt_id, m);
        }
      });

      return vehicles.map(v => ({
        ...v,
        nav: latestMetrics.get(v.id)?.computed_nav || 0,
        calledCum: latestMetrics.get(v.id)?.called_cum || 0,
      }));
    },
    enabled: !!orgId
  });

  // Calculate alternatives summary from the merged data
  const altsSummary = useMemo(() => {
    let totalNav = 0;
    let totalCommitment = 0;
    let totalCalledCum = 0;
    let staleCount = 0;
    let pendingCount = 0;

    altVehicles?.forEach(v => {
      totalCommitment += Number(v.commitment) || 0;
    });

    // Use the detailed metrics for staleness calculation
    const latestByVehicle = new Map<string, string>();
    altMetrics?.forEach(m => {
      if (!latestByVehicle.has(m.alt_id)) {
        latestByVehicle.set(m.alt_id, m.period_end);
      }
    });

    altVehicles?.forEach(v => {
      const periodEnd = latestByVehicle.get(v.id);
      if (periodEnd) {
        const days = Math.floor((Date.now() - new Date(periodEnd).getTime()) / (1000 * 60 * 60 * 24));
        if (days > 90) staleCount++;
        else if (days > 60) pendingCount++;
      } else {
        staleCount++;
      }
    });

    // Get NAV totals from the merged query
    altVehiclesWithMetrics?.forEach(v => {
      totalNav += Number(v.nav) || 0;
      totalCalledCum += Number(v.calledCum) || 0;
    });

    return {
      altsNav: totalNav,
      totalCommitment,
      unfunded: totalCommitment - totalCalledCum,
      staleCount,
      pendingCount
    };
  }, [altVehicles, altMetrics, altVehiclesWithMetrics]);

  // Fetch holdings from database
  const { data: holdingsData, isLoading: holdingsLoading, error: holdingsError } = useQuery({
    queryKey: ['holdings', orgId, familyId, entityId, asOf],
    queryFn: async () => {
      if (!orgId) return [];

      let query = supabase
        .from('holdings')
        .select('*, accounts!inner(id, type, custodian, number)')
        .eq('org_id', orgId);

      if (familyId) {
        query = query.eq('family_id', familyId);
      }
      if (entityId) {
        query = query.eq('accounts.entity_id', entityId);
      }

      const { data, error } = await query.order('mv', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId
  });

  // Fetch position metrics for NAV time series
  const { data: metricsData } = useQuery({
    queryKey: ['position-metrics-history', orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];

      let query = supabase
        .from('position_metrics')
        .select('period_end, nav_base')
        .eq('org_id', orgId)
        .order('period_end', { ascending: true });

      if (familyId) {
        query = query.eq('family_id', familyId);
      }

      const { data, error } = await query.limit(60);
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId
  });

  // Process holdings into asset classes
  const { positionsByClass, assetClassData, totalAssets, totalCost, totalUnrealizedGL } = useMemo(() => {
    const byClass: Record<string, PositionData[]> = {};
    let holdingsTotal = 0;
    let costTotal = 0;

    // Process regular holdings
    if (holdingsData && holdingsData.length > 0) {
      holdingsData.forEach(holding => {
        const accountType = (holding as HoldingData).accounts?.type;
        const assetClass = classifyAssetClass(holding, accountType);

        if (!byClass[assetClass]) {
          byClass[assetClass] = [];
        }

        const mv = Number(holding.mv) || 0;
        const cost = Number(holding.cost) || 0;
        holdingsTotal += mv;
        costTotal += cost;

        byClass[assetClass].push({
          symbol: holding.symbol,
          name: holding.name || holding.symbol,
          account: (holding as HoldingData).accounts?.custodian || 'Unknown',
          mv,
          cost,
          unrealizedGL: mv - cost,
          qty: holding.qty || 0,
          ccy: holding.ccy
        });
      });
    }

    // Add alternatives from alt_vehicles to Private Equity/Alternatives class
    if (altVehiclesWithMetrics && altVehiclesWithMetrics.length > 0) {
      if (!byClass['Private Equity']) {
        byClass['Private Equity'] = [];
      }

      altVehiclesWithMetrics.forEach(vehicle => {
        const nav = Number(vehicle.nav) || 0;
        const calledCum = Number(vehicle.calledCum) || 0;
        holdingsTotal += nav;
        costTotal += calledCum;

        byClass['Private Equity'].push({
          symbol: vehicle.vehicle_name?.substring(0, 10) || 'ALT',
          name: vehicle.vehicle_name,
          account: vehicle.manager_name || 'Unknown Manager',
          mv: nav,
          cost: calledCum,
          unrealizedGL: nav - calledCum,
          qty: 1,
          ccy: vehicle.currency || 'USD',
          isAlternative: true,
          altId: vehicle.id,
        });
      });
    }

    // Calculate asset class aggregates
    const classData = Object.entries(byClass).map(([className, positions]) => {
      const mv = positions.reduce((sum, p) => sum + p.mv, 0);
      const cost = positions.reduce((sum, p) => sum + p.cost, 0);
      return {
        class: className,
        mv,
        pct: 0, // Will calculate after total
        cost,
        unrealizedGL: mv - cost,
        accounts: positions.length,
        color: assetClassColors[className] || 'bg-gray-500'
      };
    }).sort((a, b) => b.mv - a.mv);

    const total = classData.reduce((sum, ac) => sum + ac.mv, 0);

    // Update percentages
    classData.forEach(ac => {
      ac.pct = total > 0 ? (ac.mv / total) * 100 : 0;
    });

    return {
      positionsByClass: byClass,
      assetClassData: classData,
      totalAssets: total,
      totalCost: costTotal,
      totalUnrealizedGL: total - costTotal
    };
  }, [holdingsData, altVehiclesWithMetrics]);

  // Process NAV time series
  const navTimeSeries = useMemo(() => {
    if (!metricsData || metricsData.length === 0) {
      // Return placeholder if no data
      return [{ date: asOf, nav: totalAssets, change: 0 }];
    }

    // Group by period based on granularity
    const grouped: Record<string, number[]> = {};

    metricsData.forEach(metric => {
      const date = new Date(metric.period_end);
      let key: string;

      if (navGranularity === 'yearly') {
        key = date.getFullYear().toString();
      } else if (navGranularity === 'quarterly') {
        const quarter = Math.ceil((date.getMonth() + 1) / 3);
        key = `${date.getFullYear()}-Q${quarter}`;
      } else {
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }

      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(Number(metric.nav_base) || 0);
    });

    // Calculate totals per period
    const series = Object.entries(grouped)
      .map(([date, values]) => ({
        date,
        nav: values.reduce((sum, v) => sum + v, 0),
        change: 0
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Calculate period-over-period change
    for (let i = 1; i < series.length; i++) {
      const prev = series[i - 1].nav;
      if (prev > 0) {
        series[i].change = ((series[i].nav - prev) / prev) * 100;
      }
    }

    return series.length > 0 ? series : [{ date: asOf, nav: totalAssets, change: 0 }];
  }, [metricsData, navGranularity, asOf, totalAssets]);

  // Total NAV - alternatives are now included in totalAssets via the merged query
  const nav = totalAssets + liabilities.mv;
  const navChange = navTimeSeries.length > 1
    ? ((nav - navTimeSeries[navTimeSeries.length - 2].nav) / navTimeSeries[navTimeSeries.length - 2].nav) * 100
    : 0;

  const formatCurrency = (value: number) => {
    const absValue = Math.abs(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const handlePositionClick = (position: PositionData, assetClass: string) => {
    // Alternatives open the AltInspectorDrawer with real data
    if (position.isAlternative && position.altId) {
      setSelectedAlt({ altId: position.altId, vehicleName: position.name });
      setAltDrawerOpen(true);
      return;
    }

    // Map position data to HoldingsDetailDrawer format
    const mappedPosition = {
      id: position.symbol,
      symbol: position.symbol,
      name: position.name,
      assetClass: assetClass,
      instrumentType: mapAssetClassToInstrumentType(assetClass),
      account: position.account,
      marketValue: position.mv,
      costBasis: position.cost,
      unrealizedGL: position.unrealizedGL,
      quantity: position.qty,
      currency: 'USD',
      
      // For alternatives, add mock data
      ...(isAlternativeAssetClass(assetClass) && {
        manager: position.name.split(' ').slice(0, -2).join(' '),
        vehicle: position.name,
        vintage: 2021,
        commitment: position.mv * 1.5,
        strategy: assetClass === 'Private Equity' ? 'Buyout' : 
                  assetClass === 'Venture Capital' ? 'Early Stage' :
                  assetClass === 'Private Credit' ? 'Direct Lending' : 'Diversified',
        quarterlyMarks: generateMockQuarterlyMarks(position.mv),
      }),
      
      transactions: generateMockTransactions(position, assetClass),
      documents: [],
    };
    
    setSelectedPosition(mappedPosition);
    setDrawerOpen(true);
  };

  const mapAssetClassToInstrumentType = (assetClass: string) => {
    const mapping: Record<string, string> = {
      'Cash & Equivalents': 'cash',
      'Public Equity': 'public_equity',
      'Fixed Income': 'fixed_income',
      'Private Credit': 'private_fund',
      'Private Equity': 'private_fund',
      'Venture Capital': 'private_fund',
      'Hedge Funds': 'hedge_fund',
      'Real Estate': 'real_estate',
    };
    return mapping[assetClass] || 'public_equity';
  };

  const generateMockQuarterlyMarks = (currentNav: number) => {
    return [
      {
        quarter: "Q4'23",
        date: "2023-12-31",
        nav: currentNav,
        calledPeriod: 0,
        calledCum: currentNav * 0.73,
        distributedPeriod: currentNav * 0.05,
        distributedCum: currentNav * 0.15,
        tvpi: 1.52,
        dpi: 0.21,
        irr: 18.5,
        moic: 1.31,
        status: "locked",
        staleDays: 0,
      },
      {
        quarter: "Q3'23",
        date: "2023-09-30",
        nav: currentNav * 0.92,
        calledPeriod: currentNav * 0.05,
        calledCum: currentNav * 0.73,
        distributedPeriod: currentNav * 0.03,
        distributedCum: currentNav * 0.10,
        tvpi: 1.48,
        dpi: 0.14,
        irr: 16.2,
        moic: 1.34,
        status: "approved",
        staleDays: 12,
      },
      {
        quarter: "Q2'23",
        date: "2023-06-30",
        nav: currentNav * 0.88,
        calledPeriod: currentNav * 0.08,
        calledCum: currentNav * 0.68,
        distributedPeriod: currentNav * 0.02,
        distributedCum: currentNav * 0.07,
        tvpi: 1.39,
        dpi: 0.10,
        irr: 14.8,
        moic: 1.29,
        status: "computed",
        staleDays: 45,
      },
      {
        quarter: "Q1'23",
        date: "2023-03-31",
        nav: currentNav * 0.84,
        calledPeriod: currentNav * 0.10,
        calledCum: currentNav * 0.60,
        distributedPeriod: currentNav * 0.02,
        distributedCum: currentNav * 0.05,
        tvpi: 1.33,
        dpi: 0.08,
        irr: 13.5,
        moic: 1.25,
        status: "computed",
        staleDays: 78,
      },
    ];
  };

  const generateMockTransactions = (position: PositionData, assetClass: string) => {
    if (isAlternativeAssetClass(assetClass)) {
      return [
        {
          date: "2023-12-15",
          type: "Distribution",
          amount: position.mv * 0.05,
          currency: "USD",
          sourceDoc: "Capital Distribution Notice",
        },
        {
          date: "2023-09-20",
          type: "Distribution",
          amount: position.mv * 0.03,
          currency: "USD",
          sourceDoc: "Capital Distribution Notice",
        },
        {
          date: "2023-06-10",
          type: "Capital Call",
          amount: -position.mv * 0.08,
          currency: "USD",
          sourceDoc: "Capital Call Notice",
        },
      ];
    } else {
      return [
        {
          date: "2023-11-15",
          type: "Buy",
          amount: -position.mv * 0.3,
          price: position.cost / (position.qty || 1) * 0.95,
          quantity: (position.qty || 0) * 0.3,
          currency: "USD",
        },
        {
          date: "2023-08-20",
          type: "Dividend",
          amount: position.mv * 0.02,
          currency: "USD",
        },
      ];
    }
  };


  return (
    <div className="flex-1 space-y-4 px-6 py-6">

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">Portfolio</h1>
            {selectedEntity && (
              <Badge variant="secondary">{selectedEntity.name}</Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Unified view across all holdings with alternatives drill-down
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Calendar className="h-4 w-4" />
            As of: {asOf}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <EntitySwitcher value={entityId} onValueChange={setEntityId} />
      </div>

      {/* Portfolio Summary Cards */}
      <PortfolioSummaryCards
        totalNav={nav}
        navChange={navChange}
        altsNav={altsSummary.altsNav}
        altsPct={nav > 0 ? (altsSummary.altsNav / nav) * 100 : 0}
        unfundedCommitments={altsSummary.unfunded}
        staleCount={altsSummary.staleCount}
        pendingCount={altsSummary.pendingCount}
        onViewStale={() => setCurrentTab('alerts')}
      />

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="holdings">
            All Holdings
          </TabsTrigger>
          <TabsTrigger value="alternatives" className="gap-2">
            Alternatives
            {altVehicles && altVehicles.length > 0 && (
              <Badge variant="secondary" className="ml-1">{altVehicles.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="cashflows">
            Cash Flows
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-2">
            Alerts
            {(altsSummary.staleCount > 0 || altsSummary.pendingCount > 0) && (
              <Badge variant="destructive" className="ml-1">
                {altsSummary.staleCount + altsSummary.pendingCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Holdings Tab Content */}
        <TabsContent value="holdings" className="space-y-4">
          {/* NAV Time Series Chart */}
          <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Net Asset Value Trend</CardTitle>
              <CardDescription>
                {navGranularity === "monthly" 
                  ? "12-month NAV history" 
                  : navGranularity === "quarterly"
                  ? "Quarterly NAV history"
                  : "5-year NAV history"}
              </CardDescription>
            </div>
            <Select 
              value={navGranularity} 
              onValueChange={(value) => setNavGranularity(value as "monthly" | "quarterly" | "yearly")}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={navTimeSeries}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="date" 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(value) => `$${(value / 1000000).toFixed(0)}M`}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px'
                }}
                formatter={(value: number) => formatCurrency(value)}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="nav" 
                stroke="hsl(var(--primary))" 
                strokeWidth={2}
                name="NAV"
                dot={{ fill: 'hsl(var(--primary))' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Asset Class Breakdown */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Asset Allocation</CardTitle>
              <CardDescription>
                Click any row to expand positions. Click a position to view detailed marks, transactions, and documents.
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                const altClasses = ['Private Equity', 'Venture Capital', 'Private Credit', 'Hedge Funds', 'Real Estate'];
                const firstAlt = assetClassData.find(ac => altClasses.includes(ac.class));
                if (firstAlt) setExpanded(firstAlt.class);
              }}
            >
              View Alternatives
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset Class</TableHead>
                <TableHead className="text-right">Market Value</TableHead>
                <TableHead className="text-right">% of NAV</TableHead>
                <TableHead className="text-right">Cost Basis</TableHead>
                <TableHead className="text-right">Unrealized G/L</TableHead>
                <TableHead className="text-right">Accounts</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assetClassData.map((ac) => {
                const positions = positionsByClass[ac.class] || [];
                const isExpanded = expanded === ac.class;
                
                return (
                  <>
                    <TableRow 
                      key={ac.class}
                      className="cursor-pointer hover:bg-accent/50"
                      onClick={() => setExpanded(isExpanded ? null : ac.class)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                          <div className={`h-3 w-3 rounded ${ac.color}`}></div>
                          <span className="font-medium">{ac.class}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(ac.mv)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{ac.pct.toFixed(2)}%</Badge>
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatCurrency(ac.cost)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={ac.unrealizedGL >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(ac.unrealizedGL)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {ac.accounts}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 justify-end">
                          {isAlternativeAssetClass(ac.class) && (
                            <Link to={`/portfolio/alternatives?assetClass=${encodeURIComponent(ac.class)}`}>
                              <Button variant="outline" size="sm">
                                <ExternalLink className="h-3 w-3 mr-1" />
                                Quarterly Grid
                              </Button>
                            </Link>
                          )}
                          <Button variant="ghost" size="sm">
                            {isExpanded ? 'Collapse' : 'Expand'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded Position Details */}
                    {isExpanded && positions.map((position, idx) => (
                      <TableRow 
                        key={`${ac.class}-${idx}`} 
                        className="bg-muted/30 cursor-pointer hover:bg-muted/50"
                        onClick={() => handlePositionClick(position, ac.class)}
                      >
                        <TableCell className="pl-16">
                          <div className="flex flex-col">
                            <span className="font-mono text-sm font-semibold">{position.symbol}</span>
                            <span className="text-xs text-muted-foreground">{position.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(position.mv)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-semibold">
                            {((position.mv / nav) * 100).toFixed(2)}%
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {((position.mv / ac.mv) * 100).toFixed(1)}% of class
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground font-mono text-sm">
                          {formatCurrency(position.cost)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono text-sm ${position.unrealizedGL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(position.unrealizedGL)}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          <Badge variant="outline" className="text-xs">{position.account}</Badge>
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    ))}
                  </>
                );
              })}
              
              {/* Subtotal */}
              <TableRow className="border-t-2 bg-muted/50">
                <TableCell className="font-bold">Total Assets</TableCell>
                <TableCell className="text-right font-bold">
                  {formatCurrency(totalAssets)}
                </TableCell>
                <TableCell className="text-right font-bold">
                  <Badge>100.00%</Badge>
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {formatCurrency(totalCost)}
                </TableCell>
                <TableCell className="text-right">
                  <span className={totalUnrealizedGL >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                    {formatCurrency(totalUnrealizedGL)}
                  </span>
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {assetClassData.reduce((sum, ac) => sum + ac.accounts, 0)}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>

              {/* Liabilities */}
              <TableRow className="hover:bg-accent/50">
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className={`h-3 w-3 rounded ${liabilities.color}`}></div>
                    <span className="font-medium">{liabilities.class}</span>
                  </div>
                </TableCell>
                <TableCell className="text-right font-semibold text-red-600">
                  {formatCurrency(liabilities.mv)}
                </TableCell>
                <TableCell className="text-right">
                  <Badge variant="outline">{liabilities.pct.toFixed(2)}%</Badge>
                </TableCell>
                <TableCell className="text-right text-muted-foreground">-</TableCell>
                <TableCell className="text-right text-muted-foreground">-</TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {liabilities.accounts}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>

              {/* NAV Total */}
              <TableRow className="border-t-2 bg-primary/5">
                <TableCell className="font-bold text-lg">Net Asset Value</TableCell>
                <TableCell className="text-right font-bold text-lg">
                  {formatCurrency(nav)}
                </TableCell>
                <TableCell className="text-right">
                  <Badge variant="default">NAV</Badge>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

        </TabsContent>

        {/* Alternatives Tab Content */}
        <TabsContent value="alternatives">
          <AlternativesTable orgId={orgId} familyId={familyId} />
        </TabsContent>

        {/* Cash Flows Tab Content */}
        <TabsContent value="cashflows">
          <CashFlowsTab orgId={orgId} familyId={familyId} />
        </TabsContent>

        {/* Alerts Tab Content */}
        <TabsContent value="alerts">
          <AlertsTab orgId={orgId} familyId={familyId} />
        </TabsContent>
      </Tabs>

      <HoldingsDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        position={selectedPosition}
      />

      <AltInspectorDrawer
        open={altDrawerOpen}
        onClose={() => { setAltDrawerOpen(false); setSelectedAlt(null); }}
        altId={selectedAlt?.altId || ""}
        quarter={new Date().toISOString().split('T')[0]}
        vehicleName={selectedAlt?.vehicleName}
      />
    </div>
  );
}
